import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, of, switchMap, throwError, timer } from 'rxjs';
import { NotificationService } from '../../services/notification.service';
import { TokenRefreshService } from '../services/token-refresh.service';
import { TokenStorageService } from '../services/token-storage.service';
import { PRESERVE_SESSION_ON_AUTH_FAILURE } from './auth-http-context';

/**
 * Sur un 401 réellement lié au JWT, tente une seule rotation du refresh token
 * HttpOnly puis rejoue la requête. Un 401 métier ou une ancienne requête partie
 * avant la connexion ne doit jamais déconnecter une session fraîchement créée.
 * Le mutex et la file d'attente sont centralisés dans TokenRefreshService.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const tokenStorage = inject(TokenStorageService);
  const tokenRefresh = inject(TokenRefreshService);
  const notifications = inject(NotificationService);
  const preserveSessionOnAuthFailure = req.context.get(PRESERVE_SESSION_ON_AUTH_FAILURE);

  const isAuthenticationRequest =
    req.url.includes('/api/login') ||
    req.url.includes('/api/token/refresh') ||
    req.url.includes('/api/token/logout');

  const expireSession = (failedToken: string | null): void => {
    const currentToken = tokenStorage.getToken();
    if (currentToken && currentToken !== failedToken && !tokenStorage.isExpired(currentToken)) {
      return;
    }

    const hadToken = currentToken !== null;
    const redirect = router.url;
    tokenStorage.clear();

    if (hadToken && !router.url.startsWith('/connexion')) {
      notifications.error('Votre session a expiré. Merci de vous reconnecter.');
    }

    if (!router.url.startsWith('/connexion')) {
      void router.navigate(['/connexion'], {
        queryParams: { redirect },
      });
    }
  };

  const retryWithToken = (request: HttpRequest<unknown>, token: string) =>
    next(
      request.clone({
        withCredentials: true,
        setHeaders: { Authorization: `Bearer ${token}` },
      }),
    );

  const newerUsableToken = (previousToken: string | null): string | null => {
    const latestToken = tokenStorage.getToken();
    return latestToken && latestToken !== previousToken && !tokenStorage.isExpired(latestToken)
      ? latestToken
      : null;
  };

  const retryAfterRefresh = (request: HttpRequest<unknown>, refreshedToken: string) => {
    // TokenRefreshService stocke déjà le JWT avant de réveiller sa file. Cette
    // synchronisation supplémentaire rend néanmoins le contrat explicite au
    // niveau de l'intercepteur : aucune requête ne repart avant que le stockage
    // et son en-tête Authorization ne contiennent le même token frais.
    const replacementToken = newerUsableToken(refreshedToken);
    const tokenForRetry = replacementToken ?? refreshedToken;

    if (tokenStorage.getToken() !== tokenForRetry) {
      tokenStorage.setToken(tokenForRetry);
    }

    return retryWithToken(request, tokenForRetry);
  };

  const recoverAfterConcurrentRotation = (
    failedToken: string | null,
    refreshError: HttpErrorResponse,
  ) => {
    if (refreshError.status !== 401 && refreshError.status !== 403) {
      return throwError(() => refreshError);
    }

    // Deux onglets peuvent tenter de faire tourner le même refresh token à
    // quelques millisecondes d'intervalle. L'onglet perdant reçoit 401 alors
    // que le navigateur est en train d'installer le nouveau cookie et que
    // localStorage va recevoir le JWT de l'onglet gagnant. On laisse cette
    // synchronisation se terminer puis on effectue une seule seconde tentative.
    return timer(250).pipe(
      switchMap(() => {
        const tokenFromAnotherContext = newerUsableToken(failedToken);
        if (tokenFromAnotherContext) {
          return of(tokenFromAnotherContext);
        }

        return tokenRefresh.refreshAccessToken().pipe(
          catchError((secondRefreshError: HttpErrorResponse) => {
            const lateToken = newerUsableToken(failedToken);
            if (lateToken) {
              return of(lateToken);
            }

            if (!preserveSessionOnAuthFailure) {
              expireSession(failedToken);
            }

            return throwError(() => secondRefreshError);
          }),
        );
      }),
    );
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !isAuthenticationRequest) {
        const currentToken = tokenStorage.getToken();
        const requestToken = bearerToken(req);

        // Une requête déclenchée avant la fin du login peut revenir après que
        // le nouveau JWT a été stocké. On la rejoue avec le token courant au
        // lieu de supprimer immédiatement la nouvelle session.
        if (
          currentToken &&
          requestToken !== currentToken &&
          !tokenStorage.isExpired(currentToken)
        ) {
          return retryWithToken(req, currentToken);
        }

        // Certains contrôleurs peuvent renvoyer 401 pour une règle métier.
        // Tant que le JWT local est encore valide et que l'API ne signale pas
        // explicitement une erreur JWT, on laisse l'appelant traiter l'erreur.
        if (
          currentToken &&
          !tokenStorage.isExpired(currentToken) &&
          !isJwtAuthenticationError(error)
        ) {
          return throwError(() => error);
        }

        const tokenBeforeRefresh = currentToken;

        return tokenRefresh.refreshAccessToken().pipe(
          catchError((refreshError: HttpErrorResponse) => {
            // Cas critique : une ancienne rotation peut échouer après qu'un
            // login ou une autre rotation a déjà installé un nouveau JWT.
            // L'échec ancien ne doit jamais effacer la session plus récente.
            const replacementToken = newerUsableToken(tokenBeforeRefresh);
            if (replacementToken) {
              return of(replacementToken);
            }

            if (refreshError.status === 401 || refreshError.status === 403) {
              return recoverAfterConcurrentRotation(tokenBeforeRefresh, refreshError);
            }

            if (refreshError.status === 0) {
              notifications.error(
                'Connexion interrompue. Votre session sera renouvelée au retour du réseau.',
              );
            } else {
              notifications.error(
                'Impossible de renouveler la session pour le moment. Réessayez dans quelques instants.',
              );
            }
            return throwError(() => refreshError);
          }),
          switchMap((token) => {
            const replayedToken = newerUsableToken(token) ?? token;

            return retryAfterRefresh(req, replayedToken).pipe(
              catchError((retryError: HttpErrorResponse) => {
                // Une autre fenêtre peut avoir terminé une rotation pendant le
                // rejeu. On accepte alors une dernière fois le JWT le plus récent.
                const replacementToken = newerUsableToken(replayedToken);
                if (replacementToken) {
                  return retryAfterRefresh(req, replacementToken);
                }

                if (retryError.status === 401 && isJwtAuthenticationError(retryError)) {
                  expireSession(replayedToken);
                }
                return throwError(() => retryError);
              }),
            );
          }),
        );
      }

      switch (error.status) {
        case 403: {
          const body = error.error as Record<string, unknown> | null;
          if (body?.['code'] === 'SUBSCRIPTION_READ_ONLY') {
            notifications.warning(
              typeof body['message'] === 'string'
                ? body['message']
                : 'Votre abonnement est expiré. LEM reste disponible en consultation et export jusqu’au renouvellement.',
            );
          }
          break;
        }

        case 0:
          notifications.error('Impossible de joindre le serveur LEM. Vérifiez votre connexion.');
          break;

        default:
          break;
      }

      return throwError(() => error);
    }),
  );
};

function bearerToken(request: HttpRequest<unknown>): string | null {
  const authorization = request.headers.get('Authorization');
  return authorization?.startsWith('Bearer ') ? authorization.slice(7) : null;
}

function isJwtAuthenticationError(error: HttpErrorResponse): boolean {
  const body = error.error as Record<string, unknown> | string | null;
  const message =
    typeof body === 'string'
      ? body
      : [body?.['message'], body?.['detail'], body?.['hydra:description']]
          .filter((value): value is string => typeof value === 'string')
          .join(' ');

  return /(expired jwt|jwt token|invalid token|invalid credentials|token has expired|signature verification|token not found)/i.test(
    message,
  );
}
